import React, { useState, useEffect } from "react";
import { Box, Typography, Stack, Grid, Alert } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { Save, Building2 } from "lucide-react";

import { UnifiedModal } from "../../components/UnifiedModal";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { Label } from "../../components/Label";
import { CustomTextArea } from "../../components/CustomTextArea";
import { CustomSelector } from "../../components/CustomSelector";
import { useAuth } from "../../components/contexts/AuthContext";
import { useTheme } from "../../theme/useTheme";
import PropTypes from "prop-types";
import apiClient from "../../api-client";

/**
 * Modal para crear o editar una agencia
 */
export const AgencyModal = ({
  open,
  onClose,
  agency,
  onSave,
  onAgencyChange,
  mode = "create", // "create" o "edit"
  agencyId = null, // ID de la agencia cuando es modo editar
}) => {
  const { theme, colors } = useTheme();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "superadmin";
  const agencyTypeOptions = [
    { value: "serseo", label: "Serseo" },
    { value: "external", label: "Externa" },
  ];
  const [loading, setLoading] = useState(false);

  const INVALID_URL_MESSAGE = "Por favor, introduce una url válida (Ejemplo: https://miweb.com)";
  const DOMAIN_ERROR_MESSAGE = "El dominio de la URL debe ser válido (Ejemplo: https://miweb.com)";

  const normalizeUrlWithHttps = (url) => {
    if (!url) return "";
    let normalized = url.trim();
    if (!normalized) return "";
    if (normalized.toLowerCase().startsWith("https://")) {
      return normalized;
    }
    if (normalized.toLowerCase().startsWith("http://")) {
      normalized = normalized.slice("http://".length);
    }
    return `https://${normalized.replace(/^\/\//, "")}`;
  };

  const hasValidDomain = (url) => {
    if (!url) return false;
    const withoutProtocol = url.replace(/^https?:\/\//i, "");
    return withoutProtocol.includes(".");
  };

  // Estados de validación
  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    email: false,
    phone: false,
    website: false,
  });
  const [error, setError] = useState("");

  // Cargar datos de la agencia cuando se abre en modo edición
  useEffect(() => {
    if (open && mode === "edit" && agencyId) {
      setLoading(true);
      setError("");
      setFieldErrors({
        name: false,
        email: false,
        phone: false,
        website: false,
      });
      apiClient
        .get(`agencies/${agencyId}/`)
        .then((response) => {
          const agencyData = response.data;
          // Mapear los datos del backend al formato del estado
          onAgencyChange({
            name: agencyData.name || "",
            location: agencyData.location || "",
            additional_notes: agencyData.additional_notes || "",
            phone: agencyData.phone || "",
            email: agencyData.email || "",
            website: agencyData.website || "",
            agency_type: agencyData.agency_type || "serseo",
          });
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error cargando datos de la agencia:", error);
          setError(
            error.response?.data?.message ||
            "Error al cargar los datos de la agencia"
          );
          setLoading(false);
        });
    } else if (open && mode === "create") {
      // Resetear el formulario en modo crear
      setError("");
      setFieldErrors({
        name: false,
        email: false,
        phone: false,
        website: false,
      });
      onAgencyChange({
        name: "",
        location: "",
        additional_notes: "",
        phone: "",
        email: "",
        website: "",
        agency_type: "serseo",
      });
    }
  }, [open, mode, agencyId, onAgencyChange]);

  const validateForm = () => {
    // Validación de nombre (obligatorio)
    if (!agency?.name?.trim()) {
      return {
        valid: false,
        error: "El nombre de la agencia es requerido",
        field: "name",
      };
    }

    // Validación de email si tiene contenido
    if (agency?.email && agency.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(agency.email.trim())) {
        return {
          valid: false,
          error: "Introduce un correo electrónico válido",
          field: "email",
        };
      }
    }

    // Validación de teléfono si tiene contenido
    if (agency?.phone && agency.phone.trim()) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,20}$/;
      if (!phoneRegex.test(agency.phone.trim())) {
        return {
          valid: false,
          error: "Introduce un número de teléfono válido",
          field: "phone",
        };
      }
    }

    // Validación de sitio web si tiene contenido
    if (agency?.website && agency.website.trim()) {
      const normalized = normalizeUrlWithHttps(agency.website);
      if (!hasValidDomain(normalized)) {
        return {
          valid: false,
          error: DOMAIN_ERROR_MESSAGE,
          field: "website",
        };
      }
    }

    return { valid: true };
  };

  const handleSave = () => {
    setError("");
    setFieldErrors({
      name: false,
      email: false,
      phone: false,
      website: false,
    });

    const validation = validateForm();
    if (!validation.valid) {
      setError(validation.error);
      if (validation.field) {
        setFieldErrors((prev) => ({ ...prev, [validation.field]: true }));
      }
      return;
    }

    // Normalizar solo al enviar (sin modificar lo que ve el usuario)
    const normalizedAgency = {
      ...agency,
      website: agency?.website?.trim()
        ? normalizeUrlWithHttps(agency.website)
        : "",
    };

    // Solo proceder si pasa todas las validaciones
    if (onSave) {
      onSave(normalizedAgency);
    }
  };

  const isEditMode = mode === "edit";

  const handleClose = () => {
    setError("");
    setFieldErrors({
      name: false,
      email: false,
      phone: false,
      website: false,
    });
    onClose();
  };

  return (
    <UnifiedModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Editar Agencia" : "Nueva Agencia"}
    >
      <Stack spacing={3}>
        {loading ? (
          <Typography>Cargando datos...</Typography>
        ) : (
          <>
            {/* Formulario */}
            <Grid container spacing={3}>
              {/* Nombre de la agencia */}
              <Grid size={12}>
                <Label sx={{ fontSize: "1rem" }}>Nombre de la Agencia *</Label>
                <CustomTextField
                  value={agency?.name || ""}
                  onChange={(e) =>
                    onAgencyChange((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Ej: Markezy Valencia"
                  sx={{ marginTop: "8px" }}
                  required
                  error={fieldErrors.name}
                  disabled={loading}
                />
              </Grid>

              {/* Ubicación */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Label sx={{ fontSize: "1rem" }}>Ubicación</Label>
                <CustomTextField
                  value={agency?.location || ""}
                  onChange={(e) =>
                    onAgencyChange((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                  placeholder="Ej: Valencia, España"
                  sx={{ marginTop: "8px" }}
                  disabled={loading}
                />
              </Grid>

              {/* Website */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Label sx={{ fontSize: "1rem" }}>Sitio Web</Label>
                <CustomTextField
                  value={agency?.website || ""}
                  onChange={(e) =>
                    onAgencyChange((prev) => ({
                      ...prev,
                      website: e.target.value,
                    }))
                  }
                  placeholder="https://www.ejemplo.com"
                  sx={{ marginTop: "8px" }}
                  error={fieldErrors.website}
                  disabled={loading}
                />
              </Grid>

              {/* Tipo de agencia (solo superadmin) */}
              {isSuperAdmin && (
                <Grid size={{ xs: 12 }}>
                  <Label sx={{ fontSize: "1rem" }}>Tipo de agencia</Label>
                  <Box sx={{ marginTop: "8px" }}>
                    <CustomSelector
                      value={agency?.agency_type || "serseo"}
                      onChange={(value) =>
                        onAgencyChange((prev) => ({
                          ...prev,
                          agency_type: value,
                        }))
                      }
                      options={agencyTypeOptions}
                      disabled={loading}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Notas adicionales */}
            <Box>
              <Label sx={{ fontSize: "1rem" }}>Notas Adicionales</Label>
              <CustomTextArea
                value={agency?.additional_notes || ""}
                onChange={(e) =>
                  onAgencyChange((prev) => ({
                    ...prev,
                    additional_notes: e.target.value,
                  }))
                }
                placeholder="Notas adicionales sobre la agencia..."
                sx={{ marginTop: "8px" }}
                rows={3}
                disabled={loading}
              />
            </Box>

            {/* Información adicional */}
            <Grid container spacing={3}>
              {/* Email de contacto */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Label sx={{ fontSize: "1rem" }}>Email de Contacto</Label>
                <CustomTextField
                  type="email"
                  value={agency?.email || ""}
                  onChange={(e) =>
                    onAgencyChange((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="contacto@agencia.com"
                  sx={{ marginTop: "8px" }}
                  error={fieldErrors.email}
                  disabled={loading}
                />
              </Grid>

              {/* Teléfono */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Label sx={{ fontSize: "1rem" }}>Teléfono</Label>
                <CustomTextField
                  type="tel"
                  value={agency?.phone || ""}
                  onChange={(e) =>
                    onAgencyChange((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  placeholder="+34 xxx xxx xxx"
                  sx={{ marginTop: "8px" }}
                  error={fieldErrors.phone}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            icon={<ErrorOutline fontSize="small" />}
            sx={{ alignItems: "center" }}
          >
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {/* Footer buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ paddingTop: "24px" }}
        >
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save size={16} style={{ marginRight: "8px" }} />
            {isEditMode ? "Guardar Cambios" : "Crear Agencia"}
          </Button>
        </Stack>
      </Stack>
    </UnifiedModal>
  );
};

AgencyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  agency: PropTypes.shape({
    name: PropTypes.string,
    location: PropTypes.string,
    additional_notes: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    website: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
  onAgencyChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]),
  agencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
